import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { connect } from 'react-redux';
import { injectIntl, intlShape, defineMessages } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';

// Actions
import { updateUriRef } from '../../../actions/filterActions';

// Components
import { SbSharedRefineMenuFacet } from '../../SbSharedRefineMenu/SbSharedRefineMenuFacet/SbSharedRefineMenuFacet';
import { SbSharedRefineMenuInputCheckbox } from '../../SbSharedRefineMenu/SbSharedRefineMenuInput/SbSharedRefineMenuInputCheckbox';

// Constants
import {
    DISPLAY_PREFERENCE_OPTIONS,
    SOLD,
    SHOW_SOLD,
    NET_PRICE,
    STRATEGIC_PARTNER,
    MEASUREMENTS,
    RETURNABLE,
} from '../sbSharedRefineMenuConstants';
import {
    savePreference,
    setPreferenceValueFromLocalStorage,
} from '../../../actions/displayPreferenceActions';

const messages = defineMessages({
    title: {
        id: 'sb.SbSharedRefineMenu.filter.display.title',
        defaultMessage: 'Display',
    },
});

class SbSharedRefineMenuDisplayPrefsSelectComponent extends React.Component {
    componentDidMount() {
        this.props.onMount(MEASUREMENTS);
    }

    findFilter(filterName) {
        const { itemSearch } = this.props;
        const appliedFilters = get(itemSearch, 'appliedFilters');

        return appliedFilters.filter(appliedFilter => appliedFilter.name === filterName);
    }

    getFilterUrlLabel(filter) {
        return get(filter, '[0].values[0].urlLabel');
    }

    render() {
        const { filters, user, intl, showMeasurements, initiallyExpanded } = this.props;
        const filterName = 'display';
        const isVerifiedTrade = get(user, 'isVerifiedTrade');
        const showStrategicPartnerFilter = get(user, 'showStrategicPartnerFilter');
        const title = intl.formatMessage(messages.title);

        const displayPreferenceFilters = filters
            .filter(
                ({ name, values }) => DISPLAY_PREFERENCE_OPTIONS.includes(name) && values.length
            )
            .sort((a, b) => {
                return (
                    DISPLAY_PREFERENCE_OPTIONS.indexOf(a.name) -
                    DISPLAY_PREFERENCE_OPTIONS.indexOf(b.name)
                );
            });

        return (
            <React.Fragment>
                <SbSharedRefineMenuFacet
                    title={title}
                    key={filterName}
                    storageFacetKey={filterName}
                    initiallyExpanded={initiallyExpanded}
                >
                    {displayPreferenceFilters.map((filter, index) => {
                        // net price display preference is only for trade users
                        if (filter.name === NET_PRICE && !isVerifiedTrade) {
                            return null;
                        }

                        // only show strategic parnter filter to internal users
                        if (filter.name === STRATEGIC_PARTNER && !showStrategicPartnerFilter) {
                            return null;
                        }

                        // returnable display preference is only for non-trade users
                        if (filter.name === RETURNABLE && isVerifiedTrade) {
                            return null;
                        }

                        const showSoldFilter = this.findFilter(SHOW_SOLD);
                        const forceExcludeSold =
                            !!showSoldFilter.length &&
                            this.getFilterUrlLabel(showSoldFilter) === 'false';
                        const appliedFilterValue = this.findFilter(filter.name);

                        const value = get(filter, 'values[0]');
                        let isChecked = !!(
                            appliedFilterValue.length && this.getFilterUrlLabel(appliedFilterValue)
                        );

                        // get measurements preference from localStorage
                        if (filter.name === MEASUREMENTS) {
                            isChecked = !!showMeasurements;
                        }

                        if (filter.name === SOLD && forceExcludeSold) {
                            // if showSold=false, we want the Exclude Sold box to be checked
                            isChecked = true;
                        }

                        return (
                            <SbSharedRefineMenuInputCheckbox
                                key={`${filter.name}-${value.urlLabel}-${index}`}
                                value={value.urlLabel}
                                checked={isChecked}
                                linkReference={value.linkReference}
                                linkable={value.linkable}
                                onChange={() => {
                                    this.props.onChange(filter.name, value, isChecked);
                                }}
                                text={value.displayName}
                            />
                        );
                    })}
                </SbSharedRefineMenuFacet>
            </React.Fragment>
        );
    }
}

SbSharedRefineMenuDisplayPrefsSelectComponent.defaultProps = {
    initiallyExpanded: false,
};

SbSharedRefineMenuDisplayPrefsSelectComponent.propTypes = {
    intl: intlShape.isRequired,
    itemSearch: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    onMount: PropTypes.func.isRequired,
    filters: PropTypes.array.isRequired,
    user: PropTypes.object,
    showMeasurements: PropTypes.bool,
    RefineMenuFacet: PropTypes.func,
    initiallyExpanded: PropTypes.bool,
};

function mapStateToProps({ displayPreferences }) {
    return {
        showMeasurements: displayPreferences.showMeasurements,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        onChange(filterName, filterValue, isDeselected) {
            const preferenceValue = isDeselected ? null : filterValue;
            dispatch(savePreference(filterName, preferenceValue));

            if (filterName !== MEASUREMENTS) {
                // measurements don't need to re-fetch anything
                dispatch(updateUriRef({ filterName, filterValue, isDeselected }));
            }
        },
        onMount(filterName) {
            dispatch(setPreferenceValueFromLocalStorage(filterName));
        },
    };
}

SbSharedRefineMenuDisplayPrefsSelectComponent = injectIntl(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(SbSharedRefineMenuDisplayPrefsSelectComponent)
);

export const SbSharedRefineMenuDisplayPrefsSelect = createFragmentContainer(
    SbSharedRefineMenuDisplayPrefsSelectComponent,
    {
        itemSearch: graphql`
            fragment SbSharedRefineMenuDisplayPrefsSelect_itemSearch on ItemSearchQueryConnection {
                appliedFilters {
                    name
                    values {
                        urlLabel
                    }
                }
            }
        `,
        filters: graphql`
            fragment SbSharedRefineMenuDisplayPrefsSelect_filters on SearchBrowseFilter
                @relay(plural: true) {
                name
                values {
                    displayName
                    urlLabel
                    linkReference
                    linkable
                }
            }
        `,
        user: graphql`
            fragment SbSharedRefineMenuDisplayPrefsSelect_user on User {
                isVerifiedTrade
                showStrategicPartnerFilter: hasPermission(name: "STRATEGIC_PARTNER_FILTERING")
            }
        `,
    }
);
